import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  X,
  CheckCircle,
  XCircle
} from 'lucide-react';
import axios from 'axios';

interface DetailedAnswer {
  questionId: string;
  questionNumber: number;
  level: string;
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  question: {
    _id: string;
    questionNumber: number;
    level: string;
    questionText: string;
    options: string[];
    correctAnswer: string;
    points: number;
    imageUrl?: string;
  } | null;
}

interface DetailedResult {
  startersScore: number;
  moversScore: number;
  flyersScore: number;
  totalScore: number;
  assignedLevel: string;
  levelDescription: string;
  testModel: string;
  completedAt: string;
  detailedAnswers: DetailedAnswer[];
  student: {
    nombre: string;
    apellido: string;
    dni: string;
  };
}

const PlacementTestReview: React.FC = () => {
  const navigate = useNavigate();
  const [result, setResult] = useState<DetailedResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDetailedResults();
  }, []);

  const fetchDetailedResults = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/placement/results/detailed');
      console.log('📥 Datos recibidos del servidor:', response.data.result);
      console.log('📥 Primera respuesta completa:', response.data.result.detailedAnswers[0]);
      setResult(response.data.result);
    } catch (error: any) {
      console.error('Error fetching detailed results:', error);
      setError(error.response?.data?.error || 'Error cargando las respuestas detalladas');
    } finally {
      setLoading(false);
    }
  };

  const getLevelInfo = (level: string) => {
    switch (level) {
      case 'Pre-A1':
        return { name: 'Pre-A1', color: '#ff6b6b', emoji: '🚀' };
      case 'A1':
        return { name: 'A1', color: '#4ecdc4', emoji: '🎯' };
      case 'A2':
        return { name: 'A2', color: '#45b7d1', emoji: '🌟' };
      default:
        return { name: level, color: '#666', emoji: '❓' };
    }
  };

  // Copiar exactamente la lógica de getQuestionIcon de PlacementTest
  const getQuestionIcon = (questionNumber: number, questionText: string, level: string) => {
    if (!questionText) {
      return null;
    }
    
    const qt = questionText.toLowerCase();
    
    // Primero verificar frases que ocultan icono (prioridad alta)
    const noImagePhrases = [
      "i don't know what",
      "i dont know what",
      "don't know what",
      "dont know what",
      'yesterday we',
      'which one is not a fruit',
      'which one is a school subject',
      'what do you use to write',
      'i have lived here',
      'i have lived in this city',
      'she ______ breakfast',
      'this film is',
      'he asked me if i',
      'while i ______ tv',
      'while i ______ television',
      'while they ______ dinner',
      'if i ______ enough money',
      'she is the ______ girl',
      "what's the opposite of \"young\"",
      "whats the opposite of \"young\"",
      'opposite of "young"',
      'opposite of \'young\'',
      'how are you today',
      'if it',
      'choose the correct word: "he is the',
      'he is the ______ person in the class',
      'when i was a child, i',
      'to the beach every summer',
      "what's the best ending",
      'whats the best ending',
      'choose the correct question',
      'what color is the sky',
      'she ______ to music every afternoon',
      'my father ______ to work every day',
      'he ______ a bike every day',
      'which one is a day of the week',
      "what's the opposite of \"happy\"",
      'choose the odd one out',
      'how do you go to school',
      'what do you usually eat for breakfast',
      'what did they do last weekend',
      'where is your school',
      'what did he do last night',
      'what time is it',
      'which one is correct',
      'could you tell me where',
      'tell me where'
    ];
    
    // Verificar frases que ocultan icono primero
    if (noImagePhrases.some(p => qt.includes(p))) {
      return null;
    }
    
    // Ocultar icono para preguntas específicas
    if (qt.includes('what do you wear on your feet') || qt.includes('wear on your feet')) {
      return null;
    }
    if (qt.includes("what's the opposite of 'hot'") || qt.includes("opposite of 'hot'") || 
        (qt.includes('opposite') && qt.includes('hot') && !qt.includes('cold'))) {
      return null;
    }
    if (qt.includes('where were you yesterday')) {
      return null;
    }
    
    // Si es la pregunta 1, usar la imagen personalizada
    if (questionNumber === 1) {
      return 'https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/pencil.png';
    }
    // Si es la pregunta 2, usar la imagen de sol
    if (questionNumber === 2) {
      return 'https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/sun.png';
    }
    // Si es la pregunta 3, usar la imagen de gato debajo de la mesa
    if (questionNumber === 3) {
      return 'https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/cat+under+the+table.png';
    }

    // Pregunta con tres manzanas
    if (qt.includes('three apples') || qt.includes('3 apples') || qt.includes('🍎')) {
      return 'https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/three+apples.png';
    }

    // Pregunta del balón de fútbol
    if (qt.includes('what can you do with a ball') || qt.includes('⚽')) {
      return 'https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/soccer+ball.png';
    }

    // Pregunta de la hora
    if (qt.includes('what time is it') || qt.includes('🕒')) {
      return 'https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/three+o_clock.png';
    }

    // Pregunta específica: mostrar solo un cuadro azul sin número
    if (qt.includes('which one is a color')) {
      return 'COLOR_BOX';
    }

    // Pregunta específica: mostrar cuadro azul con el número 10
    if (qt.includes('which one is a number')) {
      return 'NUMBER_BOX_10';
    }

    return null;
  };

  // Función para verificar si una respuesta es correcta (usando trim para evitar espacios)
  const checkIsCorrect = (userAnswer: string | undefined, correctAnswer: string | undefined) => {
    if (!userAnswer || !correctAnswer) {
      return false;
    }
    return userAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();
  };

  // Función de comparación más robusta
  const normalizeAnswer = (answer: string | undefined | null): string => {
    if (!answer) return '';
    return String(answer)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ') // Reemplazar múltiples espacios por uno
      .replace(/[.,;:!?]/g, ''); // Remover puntuación
  };

  // Calcular estadísticas
  const getCorrectCount = () => {
    if (!result) return 0;
    let correct = 0;
    
    result.detailedAnswers.forEach((answer, index) => {
      if (!answer.question) {
        console.warn(`Respuesta ${index + 1}: Sin pregunta`);
        return;
      }
      
      // Usar isCorrect si está disponible (viene del servidor)
      if (answer.isCorrect === true) {
        correct++;
        return;
      }
      
      // Si no está disponible, calcularlo
      const userAnswer = normalizeAnswer(answer.userAnswer);
      const correctAnswer = normalizeAnswer(answer.question.correctAnswer);
      
      // Debug solo para las primeras 3 preguntas
      if (index < 3) {
        console.log(`Pregunta ${index + 1}:`, {
          userAnswer,
          correctAnswer,
          match: userAnswer === correctAnswer,
          userAnswerRaw: answer.userAnswer,
          correctAnswerRaw: answer.question.correctAnswer,
          isCorrectFromServer: answer.isCorrect
        });
      }
      
      if (userAnswer && correctAnswer && userAnswer === correctAnswer) {
        correct++;
      }
    });
    
    console.log('📊 Total de respuestas correctas:', correct, 'de', result.detailedAnswers.length);
    return correct;
  };

  const getPercentage = () => {
    if (!result || result.detailedAnswers.length === 0) return 0;
    return Math.round((getCorrectCount() / result.detailedAnswers.length) * 100);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '20px', fontSize: '18px' }}>
            Cargando tus respuestas... 📚
          </p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: '#2d2d2d',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          color: 'white',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: 'white', marginBottom: '20px' }}>😔 Error</h2>
          <p style={{ color: '#ccc', marginBottom: '30px' }}>{error || 'No se pudieron cargar las respuestas detalladas'}</p>
          <button 
            onClick={() => navigate('/dashboard')} 
            style={{
              background: '#4a7cff',
              color: 'white',
              border: 'none',
              padding: '12px 30px',
              borderRadius: '10px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  const correctCount = getCorrectCount();
  const percentage = getPercentage();
  const studentName = result.student ? `${result.student.nombre} ${result.student.apellido}`.toUpperCase() : 'ESTUDIANTE';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#1a1a1a',
      padding: '20px',
      color: 'white'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: 'white',
            margin: '0 0 8px 0'
          }}>
            Test Completado por {studentName}
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#ccc',
            margin: 0
          }}>
            DNI: {result.student?.dni || 'N/A'} | Nivel: {result.assignedLevel || 'N/A'}
          </p>
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '32px',
            cursor: 'pointer',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X size={32} />
        </button>
      </div>

      {/* Tarjetas de Resumen */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '40px'
      }}>
        {/* Puntuación Total */}
        <div style={{
          background: '#2d2d2d',
          padding: '25px',
          borderRadius: '15px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '10px' }}>
            Puntuación Total
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {result.totalScore} puntos
          </div>
        </div>

        {/* Preguntas Correctas */}
        <div style={{
          background: '#2d2d2d',
          padding: '25px',
          borderRadius: '15px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '10px' }}>
            Preguntas Correctas
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {correctCount} de {result.detailedAnswers.length}
          </div>
        </div>

        {/* Porcentaje */}
        <div style={{
          background: '#2d2d2d',
          padding: '25px',
          borderRadius: '15px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '10px' }}>
            Porcentaje
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {percentage}%
          </div>
        </div>

        {/* Fecha */}
        <div style={{
          background: '#2d2d2d',
          padding: '25px',
          borderRadius: '15px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '10px' }}>
            Fecha de Completado
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {formatDate(result.completedAt)}
          </div>
        </div>
      </div>

      {/* Sección de Preguntas */}
      <h2 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        color: 'white',
        marginBottom: '25px'
      }}>
        Preguntas y Respuestas
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
        {result.detailedAnswers.map((answer, index) => {
          if (!answer.question) return null;

          const userAnswerValue = answer.userAnswer || '';
          const correctAnswerValue = answer.question?.correctAnswer || '';
          const isCorrect = checkIsCorrect(userAnswerValue, correctAnswerValue);
          const questionIcon = getQuestionIcon(answer.question.questionNumber, answer.question.questionText, answer.question.level);

          return (
            <div
              key={answer.question._id || index}
              style={{
                background: '#2d2d2d',
                padding: '25px',
                borderRadius: '15px',
                color: 'white'
              }}
            >
              {/* Header de la pregunta */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#3a3a3a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {answer.question.questionNumber}
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 'normal',
                  color: 'white',
                  margin: 0,
                  flex: 1
                }}>
                  {answer.question.questionText}
                </h3>
              </div>

              {/* Imágenes ocultas en la vista del estudiante - solo texto */}

              {/* Opciones de respuesta */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '15px'
              }}>
                {answer.question.options && answer.question.options.map((option, optIndex) => {
                  if (!option) return null;
                  const correctAnswer = answer.question?.correctAnswer || '';
                  const userAnswer = answer.userAnswer || '';
                  
                  // Logs de debug para las primeras preguntas
                  if (answer.question && answer.question.questionNumber <= 3) {
                    console.log(`🔍 Pregunta ${answer.question.questionNumber}, Opción ${String.fromCharCode(65 + optIndex)}:`, {
                      option,
                      correctAnswer,
                      userAnswer,
                      optionRaw: option,
                      correctAnswerRaw: correctAnswer,
                      userAnswerRaw: userAnswer
                    });
                  }
                  
                  // Normalizar usando la misma función
                  const normalizedOption = normalizeAnswer(option);
                  const normalizedCorrect = normalizeAnswer(correctAnswer);
                  const normalizedUser = normalizeAnswer(userAnswer);
                  
                  const isCorrectOption = normalizedOption === normalizedCorrect;
                  const isUserAnswerOption = normalizedOption === normalizedUser;
                  const isCorrect = isCorrectOption && isUserAnswerOption;
                  
                  // Logs de comparación para las primeras preguntas
                  if (answer.question && answer.question.questionNumber <= 3) {
                    console.log(`🔍 Comparación Pregunta ${answer.question.questionNumber}, Opción ${String.fromCharCode(65 + optIndex)}:`, {
                      normalizedOption,
                      normalizedCorrect,
                      normalizedUser,
                      isCorrectOption,
                      isUserAnswerOption,
                      isCorrect
                    });
                  }
                  
                  // Estilo según el estado de la opción
                  let bgColor = '#3a3a3a';
                  let circleBg = '#4a4a4a';
                  let textColor = 'white';
                  let letterColor = 'white';
                  let borderStyle = 'none';
                  let borderWidth = '0';
                  let borderColor = 'transparent';

                  // Prioridad: Si es la respuesta correcta Y del usuario, verde
                  if (isCorrectOption && isUserAnswerOption) {
                    // Respuesta correcta seleccionada por el usuario - verde
                    bgColor = '#a8e6cf';
                    circleBg = '#56ab2f';
                    textColor = '#155724';
                    letterColor = 'white';
                  } else if (isCorrectOption) {
                    // Solo respuesta correcta - verde claro
                    bgColor = '#a8e6cf';
                    circleBg = '#56ab2f';
                    textColor = '#155724';
                    letterColor = 'white';
                  } else if (isUserAnswerOption) {
                    // Respuesta del estudiante pero incorrecta - rojo
                    bgColor = '#f8d7da';
                    circleBg = '#dc3545';
                    textColor = '#721c24';
                    letterColor = 'white';
                    borderStyle = 'solid';
                    borderWidth = '2px';
                    borderColor = '#dc3545';
                  }

                  return (
                    <div
                      key={optIndex}
                      style={{
                        background: bgColor,
                        padding: '15px 20px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        position: 'relative',
                        border: `${borderWidth} ${borderStyle} ${borderColor}`
                      }}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: circleBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: letterColor,
                        flexShrink: 0
                      }}>
                        {String.fromCharCode(65 + optIndex)}
                      </div>
                      <span style={{
                        fontSize: '16px',
                        color: textColor,
                        flex: 1,
                        fontWeight: isUserAnswerOption ? 'bold' : 'normal'
                      }}>
                        {option}
                      </span>
                      {isCorrectOption && (
                        <>
                          <div style={{
                            fontSize: '14px',
                            color: '#155724',
                            marginLeft: 'auto',
                            marginRight: '10px',
                            fontWeight: '600'
                          }}>
                            ✔ Respuesta correcta
                          </div>
                          <CheckCircle 
                            size={24} 
                            color="#56ab2f"
                            style={{ flexShrink: 0 }}
                          />
                        </>
                      )}
                      {isUserAnswerOption && !isCorrectOption && (
                        <>
                          <div style={{
                            fontSize: '14px',
                            color: '#721c24',
                            marginLeft: 'auto',
                            marginRight: '10px',
                            fontWeight: '600'
                          }}>
                            ✗ Tu respuesta
                          </div>
                          <XCircle 
                            size={24} 
                            color="#dc3545"
                            style={{ flexShrink: 0 }}
                          />
                        </>
                      )}
                    </div>
                  );
                }).filter(Boolean)}
              </div>

              {/* Resumen de la respuesta del estudiante */}
              <div style={{
                marginTop: '20px',
                padding: '15px',
                borderRadius: '10px',
                background: isCorrect ? '#d4edda' : '#f8d7da',
                border: `2px solid ${isCorrect ? '#56ab2f' : '#dc3545'}`,
                color: isCorrect ? '#155724' : '#721c24'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <strong style={{ fontSize: '16px' }}>
                    {isCorrect ? '✅ Correcto' : '❌ Incorrecto'}
                  </strong>
                </div>
                <div style={{ fontSize: '14px', marginTop: '5px' }}>
                  <strong>Tu respuesta:</strong> {userAnswerValue || 'Sin respuesta'}
                </div>
                {!isCorrect && (
                  <div style={{ fontSize: '14px', marginTop: '5px', color: '#155724' }}>
                    <strong>Respuesta correcta:</strong> {correctAnswerValue || 'N/A'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlacementTestReview;
